import NodePanelTabs from './NodePanelTabs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getThemeDiscussionPalette } from '../../config/themes';
import { MarkdownPreview } from './NodeContent';
import { getDiscussionPanelLayout } from './NodeDiscussionPanel';

const MAX_INFO_CHARACTERS = 20_000;

function NodeInfoPanel({
  node,
  anchorRect,
  isDarkMode,
  theme,
  canEdit,
  onClose,
  onSave,
  onDiscuss,
}) {
  const persistedInfo = typeof node?.info === 'string' ? node.info : '';
  const [draft, setDraft] = useState(persistedInfo);
  const [mode, setMode] = useState(canEdit && !persistedInfo.trim() ? 'edit' : 'preview');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1024 : window.innerWidth,
    height: typeof window === 'undefined' ? 768 : window.innerHeight,
  }));
  const editorRef = useRef(null);
  const persistedInfoRef = useRef(persistedInfo);
  const dirty = draft !== persistedInfoRef.current;
  const tooLong = draft.length > MAX_INFO_CHARACTERS;
  const palette = getThemeDiscussionPalette(theme, isDarkMode);
  const layout = useMemo(
    () => getDiscussionPanelLayout(anchorRect, viewport),
    [anchorRect, viewport]
  );

  useEffect(() => {
    persistedInfoRef.current = persistedInfo;
    setDraft(persistedInfo);
    setMode(canEdit && !persistedInfo.trim() ? 'edit' : 'preview');
    setError('');
  }, [canEdit, node?.id, persistedInfo]);

  useEffect(() => {
    if (mode === 'edit') {
      editorRef.current?.focus({ preventScroll: true });
    }
  }, [mode]);

  const save = useCallback(async () => {
    if (!canEdit || !dirty) return true;
    if (tooLong || isSaving) return false;
    setIsSaving(true);
    setError('');
    try {
      const saved = await onSave(node.id, draft);
      if (saved === false) return false;
      persistedInfoRef.current = draft;
      return true;
    } catch (saveError) {
      setError(saveError?.message || 'The idea notes could not be saved.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [canEdit, dirty, draft, isSaving, node?.id, onSave, tooLong]);

  const close = useCallback(async () => {
    if (dirty && !(await save())) return;
    onClose();
  }, [dirty, onClose, save]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [close]);

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-label="Idea notes"
      data-node-panel="info"
      aria-labelledby="node-info-title"
      data-export-ignore="true"
      data-viewport-occlusion="true"
      style={{
        ...layout,
        zIndex: 181,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: palette.panel,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.38)',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 14px',
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <span id="node-info-title" hidden>Idea notes</span>
          <NodePanelTabs activeTab="info" palette={palette} onDiscuss={onDiscuss ? async () => {
            if (dirty && !(await save())) return;
            onDiscuss();
          } : undefined} />
          <div
            title={node?.text || 'Untitled idea'}
            style={{
              marginTop: 2,
              fontSize: 12,
              opacity: 0.72,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {node?.text || 'Untitled idea'}
          </div>
        </div>
        {canEdit && (
          <div
            role="group"
            aria-label="Notes display mode"
            style={{
              display: 'inline-flex',
              padding: 2,
              borderRadius: 8,
              background: palette.raised,
            }}
          >
            {['edit', 'preview'].map((candidate) => (
              <button
                key={candidate}
                type="button"
                aria-pressed={mode === candidate}
                onClick={() => setMode(candidate)}
                style={{
                  border: 'none',
                  borderRadius: 6,
                  padding: '5px 8px',
                  background: mode === candidate ? palette.accent : 'transparent',
                  color: mode === candidate ? palette.accentText : palette.text,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 650,
                }}
              >
                {candidate === 'edit' ? 'Edit' : 'Preview'}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          aria-label="Close idea notes"
          onClick={close}
          style={{
            border: 'none',
            background: 'transparent',
            color: palette.text,
            cursor: 'pointer',
            fontSize: 22,
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      </header>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: 14,
        }}
      >
        {mode === 'edit' && canEdit ? (
          <textarea
            ref={editorRef}
            aria-label="Idea notes in Markdown"
            value={draft}
            placeholder="Add context, notes, links, or a description. Markdown is supported."
            onChange={(event) => {
              setDraft(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                close();
              }
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                save();
              }
              event.stopPropagation();
            }}
            style={{
              width: '100%',
              height: '100%',
              minHeight: 240,
              resize: 'none',
              boxSizing: 'border-box',
              padding: 12,
              borderRadius: 10,
              border: `1px solid ${tooLong ? '#ef4444' : palette.border}`,
              outline: 'none',
              background: palette.input,
              color: palette.text,
              font: '13px/1.5 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
          />
        ) : draft.trim() ? (
          <article
            className="node-info-markdown"
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              overflowWrap: 'anywhere',
            }}
          >
            <MarkdownPreview source={draft} />
          </article>
        ) : (
          <p style={{ margin: 0, opacity: 0.68 }}>
            No notes have been added to this idea.
          </p>
        )}
      </div>

      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderTop: `1px solid ${palette.border}`,
          fontSize: 11,
        }}
      >
        <span style={{ flex: 1, color: tooLong ? '#ef4444' : palette.muted }}>
          {tooLong
            ? `${draft.length.toLocaleString()} / ${MAX_INFO_CHARACTERS.toLocaleString()} characters`
            : error || (canEdit ? 'Markdown supported · Ctrl/⌘+Enter to save' : 'Read only')}
        </span>
        {canEdit && (
          <button
            type="button"
            disabled={!dirty || tooLong || isSaving}
            onClick={save}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '7px 12px',
              background: palette.accent,
              color: palette.accentText,
              cursor: !dirty || tooLong || isSaving ? 'default' : 'pointer',
              opacity: !dirty || tooLong || isSaving ? 0.55 : 1,
              fontWeight: 700,
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        )}
      </footer>
    </section>
  );
}

export default NodeInfoPanel;
